import React, { useEffect, useMemo } from "react";
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import axios from "axios";
import AddPatientModal from "../../Components/Modals/AddPatientModal/AddPatientModal";
import {
  differenceInMonths,
  format,
  formatDistanceToNow,
  max,
  parseISO,
} from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  MessageOutlined,
  DeleteOutlined,
  WarningOutlined,
} from "@ant-design/icons";

import "./Patients.scss";
import { useTranslation } from "react-i18next";
import { BASE_API_URL } from "../../utils/consts";
import { useDispatch, useSelector } from "react-redux";
import {
  deletePatient,
  getPatients,
} from "../../utils/redux/slices/patientsSlice";
import { setLoader } from "../../utils/redux/slices/loaderSlice";
import { usePermissions } from "../../utils/usePermissions";
const { enUS, srLatn } = require("date-fns/locale");

interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  tags: string[];
}

type Props = {};

const Patients: React.FC<Props> = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions();
  const canView = hasPermission("patients:view");
  const permissionState = useSelector((state: any) => state.permissions);
  const language: any = localStorage.getItem("userLanguage") ?? "en";
  const locale = language === "en" ? enUS : srLatn;
  const [searchPatients, setSearchPatients] = useState<any>("");
  const [patientModalOpen, setPatientModalOpen] = useState<boolean>(false);
  const patients = useSelector((state: any) => state.patients.patients);
  const userData = useSelector((state: any) => state.userData.user);
  const clientId = userData?.clientId;

  // Učitaj pacijente kada se stranica učita
  useEffect(() => {
    if (!clientId) return;

    dispatch(setLoader(true));
    axios
      .get(`${BASE_API_URL}/${clientId}/patients`)
      .then((res) => {
        dispatch(getPatients(res.data));
      })
      .catch((err) => console.error(err))
      .finally(() => dispatch(setLoader(false)));
  }, [clientId, dispatch]);

  console.log("canView", canView);
  console.log("hasPermission.....", permissionState);
  const deleteItem = (row: any) => {
    if (!hasPermission("patients:delete")) return;
    dispatch(setLoader(true));
    axios
      .delete(`${BASE_API_URL}/patient/${row._id}/delete`)
      .then((res) => {
        dispatch(deletePatient(res.data.id));
      })
      .catch((error) => console.error(error))
      .finally(() => dispatch(setLoader(false)));
  };

  const openProfile = (row: any) => {
    if (!hasPermission("patients:open-profile")) return;
    navigate(`${row._id}`);
  };

  const getLatestControlInfo = (row: any) => {
    const dates: any = row?.lastActiveNotes?.length
      ? row.lastActiveNotes.map((item: any) => parseISO(item.date))
      : undefined;
    const latestDate: any = dates && max(dates);
    const formattedDistance =
      latestDate &&
      formatDistanceToNow(latestDate, {
        addSuffix: true,
        locale,
      });
    const monthsDifference = differenceInMonths(
      new Date(),
      latestDate ?? new Date()
    );

    let color = "green";
    if (monthsDifference > 5) color = "#3399cc";
    else if (monthsDifference > 4) color = "orange";

    return { formattedDistance, monthsDifference, color };
  };

  const renderLastControlTag = (row: any) => {
    const { formattedDistance, monthsDifference, color } =
      getLatestControlInfo(row);
    if (!formattedDistance) return null;
    return (
      <Tag color={color} key={"patientWarning"}>
        <WarningOutlined twoToneColor="#ff0000" />{" "}
        {formattedDistance?.toUpperCase() ?? ""}
      </Tag>
    );
  };

  const columns: ColumnsType<DataType> = [
    {
      title: t("Name"),
      dataIndex: "name",
      key: "name",
      render: (text) => text,
    },
    {
      title: t("Address"),
      dataIndex: "address",
      key: "address",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    // {
    //   title: t("Social Number"),
    //   dataIndex: "jmbg",
    //   key: "jmbg",
    //   render: () => "********",
    // },
    {
      title: t("Phone Number"),
      dataIndex: "phoneNo",
      key: "phoneNo",
    },
    {
      title: t("Birthday"),
      dataIndex: "birthDay",
      key: "birthDay",
      render: (value, row) => {
        return value
          ? format(new Date(value ? value : new Date()), "d-MMM-yyyy")
          : "-";
      },
    },
    {
      title: t("Last Control"),
      dataIndex: "lastActiveNotes",
      key: "lastActiveNotes",
      sorter: (a: any, b: any) => {
        const latestDateA: any =
          a.lastActiveNotes?.length > 0
            ? new Date(a.lastActiveNotes[0].date)
            : null;
        const latestDateB: any =
          b.lastActiveNotes?.length > 0
            ? new Date(b.lastActiveNotes[0].date)
            : null;

        if (latestDateA && latestDateB) {
          return latestDateB - latestDateA;
        } else if (latestDateA) {
          return 1;
        } else if (latestDateB) {
          return -1;
        } else {
          return 0;
        }
      },
      render: (_value, row) => renderLastControlTag(row),
    },
    {
      title: t("Photo"),
      dataIndex: "file",
      key: "phoneNo",
      render: (file) =>
        file && file[0] ? (
          <a target="_blank" rel="noreferrer" href={file[0]}>
            Open photo
          </a>
        ) : (
          ""
        ),
    },
    {
      title: "",
      key: "action",
      render: (row, record: any) => {
        const { monthsDifference } = getLatestControlInfo(record);
        return (
          <Space>
            <Space size="middle">
              <Button
                onClick={() => openProfile(row)}
                disabled={!hasPermission("patients:open-profile")}
              >
                {t("User Profile")}
              </Button>
            </Space>
            <Button
              disabled={
                monthsDifference < 6 || !hasPermission("patients:send-reminder")
              }
            >
              <MessageOutlined />
            </Button>
            <Button
              onClick={() => deleteItem(row)}
              disabled={!hasPermission("patients:delete")}
            >
              <DeleteOutlined />
            </Button>
          </Space>
        );
      },
    },
  ];

  const showModal = () => {
    if (!hasPermission("patients:create")) {
      return;
    }
    setPatientModalOpen(!patientModalOpen);
    // axios
    //   .get(`${BASE_API_URL}/patient/all`)
    //   .then((res) => {
    //     // TODO UpdatePatientsList
    //     // setPatients(res.data);
    //   })
    //   .catch((err) => console.error(err));
  };

  const onSearchPatients = (e: any) => {
    setSearchPatients(e.target.value);
  };

  const renderPatients = useMemo(() => {
    if (searchPatients) {
      const filterPatients = patients?.filter((item: any) =>
        item.name.toLowerCase().includes(searchPatients.toLowerCase())
      );
      return filterPatients;
    } else {
      return patients;
    }
  }, [searchPatients, patients]);
  if (!canView) {
    return <div className="mainContainer">{t("No permission to view")}</div>;
  }

  return (
    <div className="mainContainer">
      <Row justify={"space-between"}>
        <Col>
          <Button
            type="primary"
            onClick={showModal}
            disabled={!hasPermission("patients:create")}
          >
            + {t("Add Patient")}
          </Button>
        </Col>
        <Col>
          <div className="searchPatients">
            <Form.Item label={t("Search Patients")} name="description">
              <Input
                placeholder={t("Search")}
                onChange={(e: any) => onSearchPatients(e)}
                disabled={!hasPermission("patients:search")}
              />
            </Form.Item>
          </div>
        </Col>
      </Row>
      <Divider />
      <div className="hide-md">
        <Table
          columns={columns}
          dataSource={renderPatients?.length ? renderPatients : []}
          rowKey="_id"
        />
      </div>
      <div className="show-md patientsFullList">
        {renderPatients?.length ? (
          renderPatients?.map((item: any, index: number) => {
            const lastControlTag = renderLastControlTag(item);
            return (
              <div
                className="patientsListItem"
                key={item?._id || index}
                onClick={() => openProfile(item)}
              >
                <div className="patientHeader">
                  <div>
                    <div className="patientName">{item?.name}</div>
                    <div className="patientMeta">{item?.phoneNo}</div>
                    <div className="patientMeta">{item?.email || "-"}</div>
                  </div>
                  {lastControlTag}
                </div>

                <div className="patientMeta">
                  {t("Address")}: {item?.address || "-"}
                </div>
                <div className="patientMeta">
                  {t("Birth Date")}:{" "}
                  {item?.birthDay
                    ? format(new Date(item?.birthDay), "d-MMM-yy")
                    : "-"}
                </div>
                <div className="patientMeta">{t("Last Control")}:</div>
                {lastControlTag || (
                  <div className="patientMeta">{t("No data")}</div>
                )}
                {item?.file?.[0] ? (
                  <a
                    href={item.file[0]}
                    target="_blank"
                    rel="noreferrer"
                    className="patientLink"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t("Photo")}
                  </a>
                ) : null}

                <div className="patientActions">
                  <Button
                    size="small"
                    type="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      openProfile(item);
                    }}
                    disabled={!hasPermission("patients:open-profile")}
                  >
                    {t("User Profile")}
                  </Button>
                  <Button
                    size="small"
                    icon={<MessageOutlined />}
                    disabled={
                      getLatestControlInfo(item).monthsDifference < 6 ||
                      !hasPermission("patients:send-reminder")
                    }
                    shape="circle"
                    type="default"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    shape="circle"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteItem(item);
                    }}
                    disabled={!hasPermission("patients:delete")}
                  />
                </div>
              </div>
            );
          })
        ) : (
          <div className="patientEmpty">{t("No patients found")}</div>
        )}
      </div>
      <AddPatientModal isOpen={patientModalOpen} onCloseModal={showModal} />
    </div>
  );
};

export default Patients;
